import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { usePatchDepartment } from '@vlabs/api/artemy/client';
import { StraightArrowUpAndDownIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { toast } from 'react-toastify';

import { DEPARTMENT_TREE_QUERY_KEY } from '@vlabs/monokit/entities/departments/departments-tree';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useMoveDepartmentsTranslation } from '../i18n';
import type { MoveDepartmentsButtonProps, MoveDepartmentsFormValues } from '../types';
import { MoveDepartmentsForm } from './MoveDepartmentsForm';

type MoveDepartmentsButtonComponentProps = MoveDepartmentsButtonProps & Omit<ButtonProps, 'onClick'>;

const displayName = 'MoveDepartmentsButton';
const testid = buildTestid(displayName);

function MoveDepartmentsButton({
  departmentIds,
  'data-testid': dataTestId = undefined,
  ...props
}: MoveDepartmentsButtonComponentProps): React.JSX.Element {
  const { t } = useMoveDepartmentsTranslation();
  const modal = useModal({ size: 'small' });
  const queryClient = useQueryClient();

  const patchDepartmentMutation = usePatchDepartment();

  const handleSubmit = async ({
    targetDepartmentId,
  }: MoveDepartmentsFormValues): Promise<void> => {
    try {
      await Promise.all(
        departmentIds.map(async (departmentId) => patchDepartmentMutation.mutateAsync({
          departmentId,
          data: {
            parent_department_id: targetDepartmentId,
          },
        })),
      );

      await queryClient.invalidateQueries({ queryKey: DEPARTMENT_TREE_QUERY_KEY });

      toast.success(t('toast_success'));
      modal.close();
    } catch {
      toast.error(t('toast_error'));
    }
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <MoveDepartmentsForm
          departmentIds={departmentIds}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<StraightArrowUpAndDownIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="outlined"
        {...testid(dataTestId)}
        {...props}
      >
        {props.children ?? t('button')}
      </Button>
    </React.Fragment>
  );
}

MoveDepartmentsButton.displayName = displayName;

export { MoveDepartmentsButton };
